import type { RecordView } from '@kit/sync/types/record-view';
import { toast } from 'sonner';
import { createDocument } from '@/entities/document/api/create-document';
import { deleteDocument } from '@/entities/document/api/delete-document';
import { saveDocument } from '@/entities/document/api/save-document';
import { DOCUMENT_CATEGORY_LABELS } from '@/entities/document/config/document-category-labels';
import { documentCategorySchema } from '@/entities/document/schemas/document-category';
import type { KitDocument } from '@/entities/document/schemas/kit-document';
import { usePersonName } from '@/entities/session/hooks/use-person-name';
import { useSession } from '@/entities/session/hooks/use-session';
import { AttachmentsField } from '@/entities/shared/attachment/attachments-field';
import { RecordMeta } from '@/entities/shared/record/record-meta';
import { EditorSheet } from '@/shared/components/editor-sheet/editor-sheet';
import { DateField } from '@/shared/components/form/date-field';
import { FormGrid } from '@/shared/components/form/form-grid';
import { NumberField } from '@/shared/components/form/number-field';
import { numberToInput } from '@/shared/components/form/number-to-input';
import { parseNumber } from '@/shared/components/form/parse-number';
import { SelectField } from '@/shared/components/form/select-field';
import { TextField } from '@/shared/components/form/text-field';
import { TextareaField } from '@/shared/components/form/textarea-field';
import { useForm } from '@/shared/hooks/use-form';
import { useTranslation } from '@/shared/i18n/use-translation';
import { rankSuggestions } from '@/shared/lib/text/rank-suggestions';

type DocumentEditorProps = {
  readonly view: RecordView<KitDocument> | undefined;
  readonly docs: readonly RecordView<KitDocument>[];
  readonly onClose: () => void;
};

const CATEGORY_OPTIONS = documentCategorySchema.options.map((value) => ({
  value,
  label: DOCUMENT_CATEGORY_LABELS[value].label,
}));

export function DocumentEditor({ view, docs, onClose }: DocumentEditorProps) {
  const { t } = useTranslation();
  const session = useSession();
  const nameOf = usePersonName();
  const doc = view?.data;
  const form = useForm({
    title: doc?.title ?? '',
    category: doc?.category ?? 'identity',
    holder: doc?.holder ?? '',
    issued_on: doc?.issued_on ?? '',
    expires_on: doc?.expires_on ?? '',
    remind_days: numberToInput(doc?.remind_days),
    original_location: doc?.original_location ?? '',
    notes: doc?.notes ?? '',
    attachments: doc?.attachments ?? [],
  });
  const holders = [
    ...new Set([
      ...(session?.people.map((person) => person.display_name) ?? []),
      t('Both'),
      ...rankSuggestions(docs, (v) => v.data.holder),
    ]),
  ];

  async function save() {
    const saved = await form.submit(async function write() {
      const data: Partial<KitDocument> = {
        ...doc,
        ...form.values,
        remind_days: parseNumber(form.values.remind_days),
      };
      if (view) await saveDocument(view.id, data);
      else await createDocument(data);
    });
    if (!saved) return;
    toast.success(view ? t('Document saved') : t('Document added'));
    onClose();
  }

  async function remove() {
    if (!view) return;
    await deleteDocument(view.id);
    onClose();
  }

  return (
    <EditorSheet
      title={view ? view.data.title : t('New document')}
      saving={form.saving}
      onClose={onClose}
      onSubmit={save}
      onDelete={view ? remove : undefined}
    >
      <TextField
        label={t('Title')}
        required
        autoFocus={!view}
        placeholder={t("e.g. Romi's passport")}
        {...form.field('title')}
      />
      <FormGrid>
        <SelectField label={t('Type')} options={CATEGORY_OPTIONS} {...form.field('category')} />
        <TextField label={t('Holder')} suggestions={holders} {...form.field('holder')} />
      </FormGrid>
      <FormGrid>
        <DateField label={t('Issued on')} {...form.field('issued_on')} />
        <DateField label={t('Expires on')} {...form.field('expires_on')} />
      </FormGrid>
      <NumberField
        label={t('Remind days before')}
        integer
        placeholder="90"
        hint={t('Empty: 90 days, time to renew it')}
        {...form.field('remind_days')}
      />
      <TextField
        label={t('Where the original is')}
        placeholder={t('Blue folder, safe…')}
        suggestions={rankSuggestions(docs, (v) => v.data.original_location)}
        {...form.field('original_location')}
      />
      <AttachmentsField
        value={form.values.attachments}
        onChange={(value) => form.set('attachments', value)}
      />
      <TextareaField label={t('Notes')} {...form.field('notes')} />
      {view && <RecordMeta view={view} nameOf={nameOf} />}
    </EditorSheet>
  );
}
